import React from 'react';
import { motion } from 'framer-motion';
import { Star, Check, AlertTriangle, XCircle, ScanLine } from 'lucide-react';
export function HeroSection() {
  return <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-406">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden" data-id="element-407">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-brand-100/40 rounded-full blur-3xl opacity-50" data-id="element-408" />
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-accent-100/40 rounded-full blur-3xl opacity-40" data-id="element-409" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-410">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-411">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center lg:text-left" data-id="element-412">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-brand-100 text-brand-700 text-sm font-medium mb-6" data-id="element-413">
              <span className="flex h-2 w-2 rounded-full bg-brand-500 mr-2 animate-pulse" data-id="element-414"></span>
              Now available on iOS & Android
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-tight mb-6" data-id="element-415">
              Know What You're <br className="hidden lg:block" data-id="element-416" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-500" data-id="element-417">
                Really Eating
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-418">
              Scan any packaged food to instantly detect unhealthy ingredients.
              Make smarter choices with AI-powered ingredient analysis.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10" data-id="element-419">
              <a href="https://apps.apple.com/us/app/intellieats/id6744873855" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-420">
                <button className="w-full sm:w-auto flex items-center justify-center px-6 py-3.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all hover:scale-105 duration-200 shadow-xl" data-id="element-421">
                  <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor" data-id="element-422">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.23-3.14-2.47-1.72-2.5-3.03-7.11-1.27-10.13 0.86-1.49 2.4-2.43 4.09-2.46 1.3-.03 2.52.87 3.3.87.78 0 2.25-1.09 3.8-0.92 0.65.03 2.46.26 3.63 1.98-3.24 1.94-2.71 5.99 0.44 7.32-0.67 1.69-1.6 3.35-2.24 4.31zM13 3.5c.73-.83 1.21-1.96 1.08-3.11-1.05.04-2.31.69-3.06 1.57-.68.8-1.27 2.07-1.11 3.17 1.18.09 2.38-.79 3.09-1.63z" data-id="element-423" />
                  </svg>
                  <div className="text-left" data-id="element-424">
                    <div className="text-[10px] uppercase font-medium opacity-80" data-id="element-425">
                      Download on the
                    </div>
                    <div className="text-sm font-bold leading-none" data-id="element-426">
                      App Store
                    </div>
                  </div>
                </button>
              </a>

              <a href="https://play.google.com/store/apps/details?id=com.Lakh.intelli_eats&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-427">
                <button className="w-full sm:w-auto flex items-center justify-center px-6 py-3.5 bg-white text-slate-900 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all hover:scale-105 duration-200 shadow-lg" data-id="element-428">
                  <svg className="w-6 h-6 mr-3 text-slate-900" viewBox="0 0 24 24" fill="currentColor" data-id="element-429">
                    <path d="M3,20.5V3.5C3,2.91,3.34,2.39,3.84,2.15L13.69,12L3.84,21.85C3.34,21.6,3,21.09,3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08,20.75,11.5,20.75,12C20.75,12.5,20.5,12.92,20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" data-id="element-430" />
                  </svg>
                  <div className="text-left" data-id="element-431">
                    <div className="text-[10px] uppercase font-medium opacity-80" data-id="element-432">
                      Get it on
                    </div>
                    <div className="text-sm font-bold leading-none" data-id="element-433">
                      Google Play
                    </div>
                  </div>
                </button>
              </a>
            </div>

            <div className="flex items-center justify-center lg:justify-start space-x-8 text-sm font-medium text-slate-500" data-id="element-434">
              <div className="flex items-center" data-id="element-435">
                <div className="flex text-accent-500 mr-2" data-id="element-436">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-437" />)}
                </div>
                <span data-id="element-438">5.0 Rating</span>
              </div>
              <div className="w-px h-4 bg-slate-300" data-id="element-439"></div>
              <div data-id="element-440">Free Scanner</div>
              <div className="w-px h-4 bg-slate-300" data-id="element-441"></div>
              <div data-id="element-442">1k+ Scans</div>
            </div>
          </motion.div>

          {/* Phone Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative mx-auto lg:mr-0 max-w-[320px] lg:max-w-[360px]" data-id="element-443">
            {/* Floating Elements */}
            <motion.div animate={{
            y: [0, -15, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -right-8 top-20 z-20 bg-white p-3 rounded-2xl shadow-xl border border-slate-100 hidden sm:block" data-id="element-444">
              <div className="flex items-center gap-3" data-id="element-445">
                <div className="bg-red-100 p-2 rounded-full" data-id="element-446">
                  <XCircle className="w-5 h-5 text-red-600" data-id="element-447" />
                </div>
                <div data-id="element-448">
                  <div className="text-xs text-slate-500 font-medium" data-id="element-449">
                    Detected
                  </div>
                  <div className="text-sm font-bold text-slate-900" data-id="element-450">Red 40</div>
                </div>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, 15, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -left-8 bottom-32 z-20 bg-white p-3 rounded-2xl shadow-xl border border-slate-100 hidden sm:block" data-id="element-451">
              <div className="flex items-center gap-3" data-id="element-452">
                <div className="bg-brand-100 p-2 rounded-full" data-id="element-453">
                  <Check className="w-5 h-5 text-brand-600" data-id="element-454" />
                </div>
                <div data-id="element-455">
                  <div className="text-xs text-slate-500 font-medium" data-id="element-456">Safe</div>
                  <div className="text-sm font-bold text-slate-900" data-id="element-457">
                    Natural Flavors
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Phone Frame with Real Screenshot */}
            <div className="relative z-10 bg-slate-900 rounded-[3rem] p-3 shadow-2xl ring-1 ring-slate-900/5" data-id="element-458">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 h-6 w-32 bg-slate-900 rounded-b-2xl z-20" data-id="element-459"></div>
              <div className="bg-white rounded-[2.5rem] overflow-hidden h-[640px] w-full relative" data-id="element-460">
                <img src="https://cdn.magicpatterns.com/uploads/rEv5VJjByxFRFVxawWAVNC/WhatsApp_Image_2026-03-16_at_14.19.17.jpg" alt="IntelliEats app showing scan results with ingredients to avoid and limited intake categories" className="w-full h-full object-cover object-top" data-id="element-461" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}